// templateLegacyCompatibility.js
// Call this AFTER checking saved field_values and top-level field IDs.

const PRIVATE_PROVIDER_FIELDS = {
  "2a419639-8e9e-46ee-ad90-49a5f40cd93e": ["pp_company_name", "companyName"],
  "b7e7f7c2-35b2-4b4d-afdb-329e6c136b60": [
    "pp_full_name",
    "pp_name",
    "name",
    "licenseName",
  ],
  "4163d761-9726-4905-ab42-5f35d2962358": [
    "pp_full_address",
    "pp_address",
    "address",
    "fullAddress",
  ],
  "092a288c-1b1b-4802-a7ad-c1f932bba039": ["pp_phone", "phone", "mobile"],
  "ccd0da0b-f8aa-4327-a101-f2c55addae4f": ["pp_fax", "fax"],
  "85a695ef-44b4-45c3-a47a-ecca1c78ac91": ["pp_email", "email"],
  "31ce3c47-4f5f-4d57-a46c-1b09a56baf93": ["pp_title", "title"],
  "b1b2046e-a183-45c4-b0d1-f342e3401a0f": [
    "pp_license_num",
    "pp_license",
    "license",
    "licenseNumber",
  ],
};

const DESIGN_PROFESSIONAL_FIELDS = {
  // Design Professional
  "596b33da-98c1-4605-a27a-c8c82a8b13e5": ["design_pro_company", "companyName"],
  "0c1775bf-d193-47cd-a568-e964783b8186": [
    "design_pro_license_name",
    "licenseName",
  ],
  "24e466d7-f93f-47bd-ad6a-6bac2f5f36fb": [
    "design_pro_license_num",
    "licenseNumber",
  ],
  "d3fb1217-3a59-4890-a73c-0e988afe3e6b": [
    "design_pro_address",
    "address",
    "fullAddress",
  ],
  "8f16e0b6-e0ce-44aa-9232-89c356cab490": [
    "design_pro_street",
    "street",
    "address1",
  ],
  "d0ad4618-a1f4-4a87-b24a-c7cce9769e32": ["design_pro_city", "city"],
  "dfb2e43a-75a3-4424-95ba-d03a3240ba5d": ["design_pro_state", "state"],
  "6a354b46-623b-4ecc-9fbe-ef2a41b59a69": ["design_pro_zip", "zip", "zipCode"],
  "b41cfe30-1382-49c7-8a6a-e32674af9a29": [
    "design_pro_phone",
    "phone",
    "mobile",
  ],
  "b5a724b4-d442-40e2-a0da-8d9338387615": ["design_pro_fax", "fax"],
  "24d31aa9-0053-45e3-ad5d-2879c7e7986b": ["design_pro_email", "email"],

  // Engineer
  // The old flow used companyName for the Engineer name.
  "151e2dcc-750f-48de-9a28-565c09f16d86": [
    "engineer_name",
    "engineerName",
    "engineer",
    "companyName",
    "licenseName",
  ],
  "f6bd69a2-2182-42a1-be90-37ec72773293": [
    "engineer_full_address",
    "engineer_address",
    "engineerAddress",
    "address",
    "fullAddress",
  ],
  "922a2a35-b269-433f-b57e-6236b26692e5": [
    "engineer_street",
    "engineerStreet",
    "street",
    "address1",
  ],
  "ddba1d90-2c3f-449c-aeca-c50bd76a6720": [
    "engineer_city",
    "engineerCity",
    "city",
  ],
  "57e8a6ed-7868-466f-8003-c22261b1fb90": [
    "engineer_state",
    "engineerState",
    "state",
  ],
  "047b004e-cd7f-4a2a-9ddb-ec2abb0da3c8": [
    "engineer_zip",
    "engineerZip",
    "zip",
    "zipCode",
  ],
  "e46f498f-35bc-4976-8f80-73ca630aa2ee": [
    "engineer_phone",
    "engineerPhone",
    "phone",
    "mobile",
  ],
  "b727fadc-a3e1-4888-9079-3fe4d91ac155": [
    "engineer_email",
    "engineerEmail",
    "email",
  ],
  "abf612d2-7fa6-4bb9-ae2d-5010c9ded07c": [
    "engineer_license_num",
    "engineer_license_number",
    "engineer_license",
    "engineerLicenseNumber",
    "engineerLicense",
    "licenseNumber",
  ],

  // Architect
  // The old flow used companyName for the Architect name.
  "028fbd0e-3cfa-47b1-990b-de91cd2a75c2": [
    "architect_name",
    "architectName",
    "architect",
    "companyName",
    "licenseName",
  ],
  "d2c548be-7c74-429a-96a2-57abc42a85ff": [
    "architect_phone",
    "architectPhone",
    "phone",
    "mobile",
  ],
  "0c6db7ea-63f8-4721-ae32-5efb16c678b9": [
    "architect_email",
    "architectEmail",
    "email",
  ],
};

const hasValue = (object, key) =>
  Boolean(object && key) &&
  Object.prototype.hasOwnProperty.call(object, key) &&
  object[key] !== undefined &&
  object[key] !== null;

const unwrapValue = (value) =>
  value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  "value" in value
    ? value.value
    : value;

export const getLegacyTemplateFieldValue = (template, field, config) => {
  const fieldMap =
    config?.listKey === "privateProvider"
      ? PRIVATE_PROVIDER_FIELDS
      : config?.listKey === "designProfessional"
        ? DESIGN_PROFESSIONAL_FIELDS
        : null;

  if (!fieldMap) {
    return { found: false, value: undefined };
  }

  // Only semantic field names are considered here.
  // Generic properties such as companyName cannot identify
  // which document field is being filled.
  const fieldName = field?.name;

  const semanticAliases = Object.values(fieldMap).find((aliases) =>
    aliases.some(
      (alias) =>
        alias === fieldName &&
        /^(pp_|design_pro_|engineer|architect)/.test(alias),
    ),
  );

  const aliases = fieldMap[field?.field_id] || semanticAliases || [];

  const key = aliases.find((alias) => hasValue(template, alias));

  return key
    ? {
        found: true,
        value: unwrapValue(template[key]),
      }
    : {
        found: false,
        value: undefined,
      };
};
